import { useQueries } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
	type Broadcast,
	broadcastKeys,
	getBroadcast,
	sentCount,
	readWatchedBroadcasts,
	unwatchBroadcast,
	WATCHED_BROADCASTS_CHANGED,
	type WatchedBroadcast,
} from "@/entities/broadcast"
import { useSessionRole } from "@/entities/session"
import { Button } from "@/shared/ui/button"

const POLL_MS = 5000
const TOAST_MS = 20_000

/** «отправлено 120, заблокировали 3, ошибок 2»: только то, что не ноль, кроме отправленных. */
function summary(broadcast: Broadcast): string {
	const { blocked, failed, noId } = broadcast.stats
	return [
		`отправлено ${sentCount(broadcast.stats)}`,
		blocked ? `заблокировали бота ${blocked}` : "",
		failed + noId ? `ошибок ${failed + noId}` : "",
	]
		.filter(Boolean)
		.join(", ")
}

/**
 * Итог рассылки прямо в бэкофисе: пока вкладка открыта, следим за отправленными отсюда
 * рассылками и, когда отправка закончится, показываем, сколько дошло, и куда нажать дальше.
 */
export function BroadcastFinishWatcher() {
	const { isModerator } = useSessionRole()
	const navigate = useNavigate()
	const [watched, setWatched] = useState<WatchedBroadcast[]>(readWatchedBroadcasts)

	useEffect(() => {
		const onChange = (event: Event) =>
			setWatched((event as CustomEvent<WatchedBroadcast[]>).detail ?? readWatchedBroadcasts())
		window.addEventListener(WATCHED_BROADCASTS_CHANGED, onChange)
		return () => window.removeEventListener(WATCHED_BROADCASTS_CHANGED, onChange)
	}, [])

	const results = useQueries({
		queries: watched.map((item) => ({
			queryKey: broadcastKeys.detail(item.botId, item.id),
			queryFn: () => getBroadcast(item.botId, item.id),
			enabled: isModerator,
			refetchInterval: POLL_MS,
			refetchIntervalInBackground: true,
		})),
	})

	useEffect(() => {
		results.forEach((result, index) => {
			const item = watched[index]
			if (!item) return
			const broadcast = result.data
			if (broadcast?.status === "DRAFT") {
				unwatchBroadcast(item.id)
				return
			}
			if (!broadcast || (broadcast.status !== "DONE" && broadcast.status !== "CANCELLED")) return
			unwatchBroadcast(item.id)
			const report = `/admin/bots/${item.botId}/broadcasts/${item.id}`
			const id = `broadcast-finished-${item.id}`
			const go = (to: string) => {
				toast.dismiss(id)
				navigate(to)
			}
			const notify = broadcast.stats.failed > 0 ? toast.warning : toast.success
			notify(
				`Рассылка «${broadcast.name || item.name}» ${broadcast.status === "DONE" ? "отправлена" : "остановлена"}`,
				{
					id,
					duration: TOAST_MS,
					description: (
						<div className="space-y-2">
							<p>{summary(broadcast)}</p>
							<div className="flex flex-wrap gap-2">
								<Button size="sm" variant="outline" onClick={() => go(report)}>
									Открыть отчёт
								</Button>
								{broadcast.stats.failed > 0 ? (
									<Button size="sm" onClick={() => go(`${report}?retry=failed`)}>
										Отправить ещё раз, где была ошибка
									</Button>
								) : null}
							</div>
						</div>
					),
				},
			)
		})
	}, [results, watched, navigate])

	return null
}
